import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, 
  Edit, 
  Save, 
  X, 
  Shield, 
  FileText, 
  Activity, 
  Settings,
  AlertCircle,
  Loader2,
  Eye,
  EyeOff
} from 'lucide-react';

import { useAuth } from '../../context/AuthContext';
import {
  getWebIDByIdPublic,
  updateEmergencyProfile,
  updatePrivacySettings,
  WebIDData
} from '../../services/webids';
import { Button } from '../ui';
import { CopyText } from '../ui/CopyButton';
import OnboardingGuard from '../guards/OnboardingGuard';
import EmergencyProfileFormUnified, { EmergencyProfileFormData } from '../ui/EmergencyProfileFormUnified';
import MedicalDocuments from './MedicalDocuments';
import AccessLogs from './AccessLogs';
import WebIDSettings from './WebIDSettings';

/**
 * @component WebIDDetail
 * @description Vista detallada de un WebID con todas sus funcionalidades
 */

type ActiveTab = 'profile' | 'documents' | 'logs' | 'settings';

const WebIDDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [webId, setWebId] = useState<WebIDData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<ActiveTab>('profile');
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);



  useEffect(() => {
    if (id && user) {
      loadWebID();
    }
  }, [id, user]);

  const loadWebID = async () => {
    try {
      setLoading(true);
      setError(null);

      const webIdData = await getWebIDByIdPublic(id!);
      
      // Verificar que el usuario tiene permisos para ver este WebID
      if (webIdData.userId !== user!.uid) {
        throw new Error('No tienes permisos para ver este WebID');
      }

      setWebId(webIdData);
    } catch (err) {
      console.error('Error loading WebID:', err);
      setError(err instanceof Error ? err.message : 'Error al cargar WebID');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async (profileData: Partial<WebIDData['emergencyProfile']>) => {
    if (!webId || !user) return;

    try {
      setSaving(true);
      await updateEmergencyProfile(webId.id, profileData, user.uid);
      await loadWebID(); // Recargar datos
      setIsEditing(false);
    } catch (err) {
      console.error('Error saving profile:', err);
      alert('Error al guardar el perfil');
    } finally {
      setSaving(false);
    }
  };

  // Adaptador para el formulario unificado
  const handleFormSubmit = async (formData: EmergencyProfileFormData) => {
    // Mapear contactos de emergencia con IDs únicos
    const mappedContacts = formData.emergencyContacts.map((contact, index) => ({
      id: contact.id || `contact_${Date.now()}_${index}`,
      name: contact.name,
      relationship: contact.relationship,
      phone: contact.phone,
      email: contact.email || '',
      isPrimary: contact.isPrimary,
      canReceiveAlerts: true,
      notes: '',
    }));

    // Convertir el primer contacto a formato string para compatibilidad
    const primaryContact = mappedContacts.find(c => c.isPrimary) || mappedContacts[0];
    const emergencyContactString = primaryContact
      ? `${primaryContact.name} - ${primaryContact.relationship} - ${primaryContact.phone}`
      : '';

    const profileData = {
      fullName: formData.name, // Corregir: usar fullName en lugar de name
      phone: formData.phone ? `${formData.phoneCountryCode} ${formData.phone}` : '',
      emergencyContacts: mappedContacts, // Enviar array completo de contactos
      bloodType: formData.bloodType,
      medicalConditions: formData.diseases, // Corregir: usar medicalConditions en lugar de diseases
      allergies: formData.allergies,
      medications: formData.medications,
      medicalNotes: formData.medicalNotes || formData.medicalInfo || '', // Corregir: usar medicalNotes
      isPublic: formData.isPublic,
      // Agregar campos de privacidad granular
      showAllergies: formData.showAllergies,
      showMedicalConditions: formData.showMedicalConditions,
      showMedications: formData.showMedications,
      showMedicalNotes: formData.showMedicalNotes,
      showEmergencyContacts: formData.showEmergencyContacts,
      allowEmergencyAccess: formData.allowEmergencyAccess,
      language: 'es',
      timezone: 'America/Santiago',
    };

    await handleSaveProfile(profileData);
  };

  const handleSavePrivacySettings = async (privacyData: { isPublic?: boolean }) => {
    if (!webId || !user) return;

    try {
      setSaving(true);
      await updatePrivacySettings(webId.id, privacyData, user.uid);
      await loadWebID(); // Recargar datos
    } catch (err) {
      console.error('Error saving privacy settings:', err);
      alert('Error al guardar configuración de privacidad');
    } finally {
      setSaving(false);
    }
  };

  const tabs = [
    { id: 'profile', label: 'Perfil de Emergencia', icon: Shield },
    { id: 'documents', label: 'Documentos Médicos', icon: FileText },
    { id: 'logs', label: 'Historial de Acceso', icon: Activity },
    { id: 'settings', label: 'Configuración', icon: Settings },
  ];

  const getStatusColor = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'assigned':
        return 'bg-blue-100 text-blue-800';
      case 'paused':
        return 'bg-yellow-100 text-yellow-800';
      case 'lost':
        return 'bg-red-100 text-red-800';
      case 'unclaimed':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusText = (status: WebIDData['status']) => {
    switch (status) {
      case 'active':
        return 'Activa';
      case 'assigned':
        return 'Asignada';
      case 'paused':
        return 'Pausada';
      case 'lost':
        return 'Perdida';
      case 'unclaimed':
        return 'Sin activar';
      default:
        return 'Desconocido';
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (error || !webId) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/panel/webids')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>
        </div>

        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <AlertCircle className="h-12 w-12 text-red-600 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-red-900 mb-2">
            Error al cargar WebID
          </h3>
          <p className="text-red-700 mb-4">
            {error || 'WebID no encontrado'}
          </p>
          <Button onClick={loadWebID}>
            Reintentar
          </Button>
        </div>
      </div>
    );
  }

  return (
    <OnboardingGuard webId={webId} loading={loading}>
      <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button
            variant="outline"
            onClick={() => navigate('/panel/webids')}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Volver
          </Button>

          <div>
            <h1 className="text-2xl font-bold text-gray-900">
              {webId.emergencyProfile.fullName || 'Sin nombre'}
            </h1>
            <div className="flex items-center gap-3 mt-1">
              <div className="flex items-center gap-2">
                <span className="text-gray-600 text-sm">WebID:</span>
                <CopyText
                  text={webId.id}
                  truncate={true}
                  maxLength={20}
                  textClassName="text-gray-600"
                  buttonVariant="ghost"
                  onCopy={() => console.log(`WebID ${webId.id} copiado`)}
                />
              </div>
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(webId.status)}`}>
                {getStatusText(webId.status)}
              </span>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => window.open(webId.publicUrl, '_blank')}
          >
            <Eye className="h-4 w-4 mr-2" />
            Ver perfil público
          </Button>

          {activeTab === 'profile' && (
            <Button
              onClick={() => setIsEditing(!isEditing)}
              disabled={saving}
            >
              {isEditing ? (
                <>
                  <span className="text-lg mr-2">×</span>
                  Cancelar
                </>
              ) : (
                <>
                  <Edit className="h-4 w-4 mr-2" />
                  Editar
                </>
              )}
            </Button>
          )}
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Escaneos</p>
              <p className="text-2xl font-bold text-blue-600">{webId.scanCount}</p>
            </div>
            <Activity className="h-8 w-8 text-blue-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Último Escaneo</p>
              <p className="text-sm font-medium text-gray-900">
                {webId.lastScanned 
                  ? webId.lastScanned.toDate().toLocaleDateString('es-CL')
                  : 'Nunca'
                }
              </p>
            </div>
            <Shield className="h-8 w-8 text-green-600" />
          </div>
        </div>

        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Visibilidad</p>
              <p className="text-sm font-medium text-gray-900">
                {webId.emergencyProfile.isPublic ? 'Público' : 'Privado'}
              </p>
            </div>
            {webId.emergencyProfile.isPublic ? (
              <Eye className="h-8 w-8 text-purple-600" />
            ) : (
              <EyeOff className="h-8 w-8 text-gray-600" />
            )}
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id as ActiveTab)}
                  className={`
                    flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm
                    ${isActive
                      ? 'border-primary-500 text-primary-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                >
                  <Icon className="h-4 w-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {activeTab === 'profile' && (
            <EmergencyProfileFormUnified
              mode="edit"
              initialData={(() => {
                // Mapear contactos de emergencia existentes
                const emergencyContacts = (webId.emergencyProfile as any)?.emergencyContacts || [];

                // Si no hay contactos en el nuevo formato, intentar parsear del formato antiguo
                let mappedContacts = emergencyContacts;
                if (emergencyContacts.length === 0 && (webId.emergencyProfile as any)?.emergencyContact) {
                  const emergencyContact = (webId.emergencyProfile as any).emergencyContact || '';
                  const contactParts = emergencyContact.split(' - ');
                  if (contactParts.length >= 3) {
                    mappedContacts = [{
                      name: contactParts[0] || '',
                      relationship: contactParts[1] || '',
                      phone: contactParts[2]?.replace(/^\+\d+\s/, '') || '',
                      email: '',
                      isPrimary: true,
                    }];
                  }
                }

                return {
                  name: (webId.emergencyProfile as any)?.fullName || (webId.emergencyProfile as any)?.name || '',
                  phone: webId.emergencyProfile?.phone?.replace(/^\+\d+\s/, '') || '',
                  phoneCountryCode: webId.emergencyProfile?.phone?.match(/^\+\d+/)?.[0] || '+56',
                  emergencyContacts: mappedContacts,
                  bloodType: webId.emergencyProfile?.bloodType || '',
                  diseases: (webId.emergencyProfile as any)?.medicalConditions || (webId.emergencyProfile as any)?.diseases || [],
                  allergies: webId.emergencyProfile?.allergies || [],
                  medications: webId.emergencyProfile?.medications || [],
                  medicalInfo: (webId.emergencyProfile as any)?.medicalNotes || (webId.emergencyProfile as any)?.medicalInfo || '',
                  medicalNotes: (webId.emergencyProfile as any)?.medicalNotes || (webId.emergencyProfile as any)?.medicalInfo || '',
                  isPublic: webId.emergencyProfile?.isPublic || false,
                  showAllergies: (webId.emergencyProfile as any)?.showAllergies ?? true,
                  showMedicalConditions: (webId.emergencyProfile as any)?.showMedicalConditions ?? true,
                  showMedications: (webId.emergencyProfile as any)?.showMedications ?? true,
                  showMedicalNotes: (webId.emergencyProfile as any)?.showMedicalNotes ?? true,
                  showEmergencyContacts: (webId.emergencyProfile as any)?.showEmergencyContacts ?? true,
                  allowEmergencyAccess: (webId.emergencyProfile as any)?.allowEmergencyAccess ?? true,
                };
              })()}
              onSubmit={handleFormSubmit}
              loading={saving}
              showPrivacySettings={true}
            />
          )}

          {activeTab === 'documents' && (
            <MedicalDocuments
              webId={webId}
              onUpdate={loadWebID}
            />
          )}

          {activeTab === 'logs' && (
            <AccessLogs
              webId={webId}
            />
          )}

          {activeTab === 'settings' && (
            <WebIDSettings
              webId={webId}
              onSave={handleSavePrivacySettings}
              onUpdate={loadWebID}
            />
          )}
        </div>
      </div>
      </div>
    </OnboardingGuard>
  );
};

export default WebIDDetail;
